/**
 * AU postcode delivery estimate (PDP, cart drawer, /cart).
 * Zones 1 & 2: +12 calendar days; zone 3 (incl. unknown): +20 days.
 *
 * PDP + cart drawer: confirmed "Change" via native <details>/<summary>.
 * /cart page: explicit Change button (summary taps fail inside sticky footer on iOS).
 */
(function () {
  const STORAGE_KEY = 'azopy_au_postcode';
  const ZONE_DAYS = { 1: 12, 2: 12, 3: 20 };
  const DEFAULT_ZONE = 3;

  /** @type {WeakMap<HTMLElement, AbortController>} */
  const rootControllers = new WeakMap();
  /** @type {WeakMap<HTMLElement, number>} */
  const renderGeneration = new WeakMap();

  /** @type {number[][] | null} */
  let ranges = null;
  /** @type {Promise<number[][]> | null} */
  let loadPromise = null;

  /**
   * @param {HTMLElement} root
   * @returns {number}
   */
  function bumpRenderGeneration(root) {
    const next = (renderGeneration.get(root) ?? 0) + 1;
    renderGeneration.set(root, next);
    return next;
  }

  /**
   * @param {HTMLElement} root
   * @param {number} generation
   * @returns {boolean}
   */
  function isRenderStale(root, generation) {
    return renderGeneration.get(root) !== generation;
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLElement | null}
   */
  function getPanel(root) {
    const panel = root.querySelector('[data-delivery-panel]');
    return panel instanceof HTMLElement ? panel : null;
  }

  /**
   * @param {HTMLElement} panel
   * @returns {boolean}
   */
  function isPanelOpen(panel) {
    if (panel instanceof HTMLDetailsElement) return panel.open;
    return panel.dataset.panelOpen === 'true';
  }

  /**
   * @param {HTMLElement} panel
   * @param {boolean} open
   */
  function setPanelOpen(panel, open) {
    if (panel instanceof HTMLDetailsElement) {
      panel.open = open;
      return;
    }

    if (open) {
      panel.dataset.panelOpen = 'true';
    } else {
      delete panel.dataset.panelOpen;
    }
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLInputElement | null}
   */
  function getPostcodeInput(root) {
    const input = root.querySelector('[data-delivery-postcode]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /**
   * @param {number[][]} sortedRanges
   * @param {number} postcode
   * @returns {number}
   */
  function lookupZone(sortedRanges, postcode) {
    let lo = 0;
    let hi = sortedRanges.length - 1;

    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const [from, to, zone] = sortedRanges[mid];

      if (postcode < from) {
        hi = mid - 1;
      } else if (postcode > to) {
        lo = mid + 1;
      } else {
        return zone;
      }
    }

    return DEFAULT_ZONE;
  }

  /**
   * @param {number} zone
   * @returns {Date}
   */
  function estimatedDeliveryDate(zone) {
    const days = ZONE_DAYS[zone] ?? ZONE_DAYS[DEFAULT_ZONE];
    const date = new Date();
    date.setHours(12, 0, 0, 0);
    date.setDate(date.getDate() + days);
    return date;
  }

  /**
   * @param {Date} date
   * @param {boolean} [compact=false]
   * @returns {string}
   */
  function formatDate(date, compact = false) {
    const now = new Date();
    const options =
      compact && date.getFullYear() === now.getFullYear()
        ? { day: 'numeric', month: 'short' }
        : { day: 'numeric', month: 'short', year: 'numeric' };

    return new Intl.DateTimeFormat('en-AU', options).format(date);
  }

  /**
   * @param {number} postcode
   * @returns {string}
   */
  function formatPostcodeDisplay(postcode) {
    return String(postcode).padStart(4, '0');
  }

  /**
   * @param {string} raw
   * @returns {number | null}
   */
  function parsePostcode(raw) {
    const digits = raw.replace(/\D/g, '');
    if (digits.length !== 4) return null;
    const value = parseInt(digits, 10);
    if (Number.isNaN(value) || value < 200 || value > 9999) return null;
    return value;
  }

  /**
   * @param {string} url
   * @returns {Promise<number[][]>}
   */
  function loadRanges(url) {
    if (ranges) return Promise.resolve(ranges);
    if (loadPromise) return loadPromise;

    loadPromise = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Failed to load postcode zones');
        return response.json();
      })
      .then((data) => {
        ranges = data;
        return ranges;
      });

    return loadPromise;
  }

  /**
   * @param {HTMLElement | null | undefined} el
   * @param {boolean} hidden
   */
  function setHidden(el, hidden) {
    if (!el) return;
    el.classList.toggle('hidden', hidden);
    if (hidden) {
      el.setAttribute('hidden', '');
    } else {
      el.removeAttribute('hidden');
    }
  }

  /**
   * @param {HTMLElement} root
   * @returns {boolean}
   */
  function usesChangeButton(root) {
    return root.dataset.changeMode === 'button';
  }

  /**
   * @param {HTMLElement} root
   * @param {'entry' | 'confirmed'} state
   */
  function setUiState(root, state) {
    root.dataset.deliveryState = state;
    const panel = getPanel(root);
    const confirmedStack = root.querySelector('[data-delivery-confirmed-stack]');
    const confirmedRow = root.querySelector('[data-delivery-confirmed-row]');
    const resultEl = root.querySelector('[data-delivery-result]');

    if (state === 'entry') {
      root.dataset.deliveryEditing = '';
      setHidden(root.querySelector('[data-delivery-prompt]'), false);
      setHidden(resultEl, true);
      setHidden(root.querySelector('[data-delivery-error]'), true);
      setHidden(confirmedStack, true);
      setHidden(confirmedRow, true);
      if (panel) setPanelOpen(panel, true);
      return;
    }

    root.dataset.deliveryEditing = '';
    setHidden(root.querySelector('[data-delivery-prompt]'), true);
    setHidden(resultEl, false);
    setHidden(root.querySelector('[data-delivery-error]'), true);
    setHidden(confirmedStack, false);
    setHidden(confirmedRow, false);
    if (panel) setPanelOpen(panel, false);
  }

  /**
   * @param {HTMLElement} root
   * @param {string} displayPostcode
   * @param {string} dateLabel
   */
  function setConfirmedContent(root, displayPostcode, dateLabel) {
    const resultEl = root.querySelector('[data-delivery-result]');
    const postcodeDisplayEl = root.querySelector('[data-delivery-postcode-display]');
    const input = getPostcodeInput(root);
    const resultTemplate = root.dataset.resultTemplate || 'Estimated delivery : {{date}}';
    const postcodeTemplate = root.dataset.postcodeTemplate || 'Postcode: {{postcode}}';

    if (resultEl) {
      resultEl.textContent = resultTemplate.replace('{{date}}', dateLabel);
    }
    if (postcodeDisplayEl) {
      postcodeDisplayEl.textContent = postcodeTemplate.replace('{{postcode}}', displayPostcode);
    }
    if (input) input.value = displayPostcode;

    setUiState(root, 'confirmed');
  }

  /**
   * @param {HTMLElement} root
   * @param {number} postcode
   * @param {boolean} [persist=true]
   * @param {boolean} [fromUserSubmit=false]
   */
  function renderEstimate(root, postcode, persist = true, fromUserSubmit = false) {
    const zonesUrl = root.dataset.zonesUrl;
    const errorEl = root.querySelector('[data-delivery-error]');

    if (!zonesUrl) return;

    const generation = bumpRenderGeneration(root);

    return loadRanges(zonesUrl)
      .then((loadedRanges) => {
        if (isRenderStale(root, generation)) return;
        const panel = getPanel(root);
        if (
          !fromUserSubmit &&
          root.dataset.deliveryState === 'confirmed' &&
          panel &&
          isPanelOpen(panel)
        ) {
          return;
        }

        const zone = lookupZone(loadedRanges, postcode);
        const date = estimatedDeliveryDate(zone);
        const displayPostcode = formatPostcodeDisplay(postcode);

        setConfirmedContent(
          root,
          displayPostcode,
          formatDate(date, root.dataset.dateFormat === 'compact')
        );

        root.dataset.deliveryHydrated = 'true';

        if (persist) {
          try {
            localStorage.setItem(STORAGE_KEY, displayPostcode);
          } catch (_) {
            /* ignore storage errors */
          }

          document.querySelectorAll('[data-delivery-estimate]').forEach((other) => {
            if (other === root || !(other instanceof HTMLElement)) return;
            const otherPanel = getPanel(other);
            if (other.dataset.deliveryState === 'confirmed' && otherPanel && isPanelOpen(otherPanel)) return;

            bumpRenderGeneration(other);
            setConfirmedContent(
              other,
              displayPostcode,
              formatDate(date, other.dataset.dateFormat === 'compact')
            );
            other.dataset.deliveryHydrated = 'true';
          });
        }
      })
      .catch(() => {
        if (isRenderStale(root, generation)) return;

        setUiState(root, 'entry');
        if (errorEl) {
          errorEl.textContent = root.dataset.errorLoad || 'Could not load delivery estimate.';
          setHidden(errorEl, false);
        }
      });
  }

  /**
   * @param {HTMLElement} root
   */
  function resetToInitialState(root) {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) {
      /* ignore storage errors */
    }

    document.querySelectorAll('[data-delivery-estimate]').forEach((instance) => {
      if (!(instance instanceof HTMLElement)) return;

      bumpRenderGeneration(instance);

      const input = getPostcodeInput(instance);
      const resultEl = instance.querySelector('[data-delivery-result]');
      const postcodeDisplayEl = instance.querySelector('[data-delivery-postcode-display]');

      if (input) input.value = '';
      if (resultEl) resultEl.textContent = '';
      if (postcodeDisplayEl) postcodeDisplayEl.textContent = '';
      delete instance.dataset.deliveryHydrated;
      setUiState(instance, 'entry');
    });
  }

  /**
   * @param {HTMLElement} root
   */
  function showFormatError(root) {
    const errorEl = root.querySelector('[data-delivery-error]');
    const panel = getPanel(root);

    root.dataset.deliveryEditing = 'true';
    if (panel) setPanelOpen(panel, true);

    if (errorEl) {
      errorEl.textContent = root.dataset.errorFormat || 'Enter a valid 4-digit Australian postcode.';
      setHidden(errorEl, false);
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function applyPostcode(root) {
    const input = getPostcodeInput(root);
    if (!input) return;

    const raw = input.value.trim();
    if (raw === '') {
      resetToInitialState(root);
      return;
    }

    const postcode = parsePostcode(raw);
    if (postcode === null) {
      showFormatError(root);
      return;
    }

    renderEstimate(root, postcode, true, true);
  }

  /**
   * @param {HTMLElement} root
   */
  function hydrateFromStorage(root) {
    const panel = getPanel(root);
    if (root.dataset.deliveryState === 'confirmed' && panel && isPanelOpen(panel)) return;
    if (root.dataset.deliveryHydrated === 'true') return;

    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return;
      const postcode = parsePostcode(saved.trim());
      if (postcode === null) return;
      root.dataset.deliveryHydrated = 'true';
      renderEstimate(root, postcode, false);
    } catch (_) {
      /* ignore storage errors */
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {Event} [event]
   */
  function openPanelForEdit(root, event) {
    event?.preventDefault();
    event?.stopPropagation();

    const panel = getPanel(root);
    if (!panel) return;

    root.dataset.deliveryEditing = 'true';
    bumpRenderGeneration(root);
    setPanelOpen(panel, true);
    setHidden(root.querySelector('[data-delivery-confirmed-row]'), true);
    if (usesChangeButton(root)) {
      setHidden(root.querySelector('[data-delivery-confirmed-stack]'), true);
    }

    const input = getPostcodeInput(root);
    input?.focus();
    input?.select();
  }

  /**
   * @param {HTMLElement} root
   */
  function handlePanelToggle(root) {
    const panel = getPanel(root);
    if (!panel) return;

    if (root.dataset.deliveryState === 'entry' && !isPanelOpen(panel)) {
      setPanelOpen(panel, true);
      return;
    }

    if (isPanelOpen(panel)) {
      openPanelForEdit(root);
      return;
    }

    root.dataset.deliveryEditing = '';
    if (root.dataset.deliveryState === 'confirmed' && usesChangeButton(root)) {
      setHidden(root.querySelector('[data-delivery-confirmed-row]'), false);
      setHidden(root.querySelector('[data-delivery-confirmed-stack]'), false);
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {Event} event
   */
  function handleSubmitFromRoot(root, event) {
    event.preventDefault();
    event.stopPropagation();
    applyPostcode(root);
  }

  /**
   * @param {Event} event
   */
  function handleSubmitClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const submitBtn = target.closest('[data-delivery-submit]');
    if (!submitBtn) return;

    const root = submitBtn.closest('[data-delivery-estimate]');
    if (!(root instanceof HTMLElement)) return;

    handleSubmitFromRoot(root, event);
  }

  /**
   * @param {KeyboardEvent} event
   */
  function handleKeydown(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.matches('[data-delivery-postcode]') || event.key !== 'Enter') return;

    const root = target.closest('[data-delivery-estimate]');
    if (!(root instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();
    applyPostcode(root);
  }

  /**
   * @param {Event} event
   */
  function handleChangeClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const changeBtn = target.closest('[data-delivery-change]');
    if (!changeBtn) return;

    const root = changeBtn.closest('[data-delivery-estimate]');
    if (!(root instanceof HTMLElement) || !usesChangeButton(root)) return;

    openPanelForEdit(root, event);
  }

  /**
   * @param {HTMLElement} root
   */
  function bindRoot(root) {
    rootControllers.get(root)?.abort();

    const controller = new AbortController();
    const { signal } = controller;
    rootControllers.set(root, controller);

    const panel = getPanel(root);
    if (panel instanceof HTMLDetailsElement) {
      panel.addEventListener('toggle', () => handlePanelToggle(root), { signal });
    }

    const submitBtn = root.querySelector('[data-delivery-submit]');
    submitBtn?.addEventListener('click', (event) => handleSubmitFromRoot(root, event), { signal });

    const changeBtn = root.querySelector('[data-delivery-change]');
    changeBtn?.addEventListener('click', (event) => openPanelForEdit(root, event), { signal });

    const input = getPostcodeInput(root);
    input?.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Enter') return;
        handleSubmitFromRoot(root, event);
      },
      { signal }
    );

    root.addEventListener('click', handleSubmitClick, { capture: true, signal });
    root.addEventListener('click', handleChangeClick, { capture: true, signal });
    root.addEventListener('keydown', handleKeydown, { signal });
  }

  function boot() {
    document.querySelectorAll('[data-delivery-estimate]').forEach((root) => {
      if (!(root instanceof HTMLElement)) return;
      bindRoot(root);
      hydrateFromStorage(root);
    });
  }

  document.addEventListener('click', handleSubmitClick, true);
  document.addEventListener('click', handleChangeClick, true);
  document.addEventListener('keydown', handleKeydown, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('cart:update', () => {
    requestAnimationFrame(boot);
  });

  document.addEventListener('shopify:section:load', () => {
    requestAnimationFrame(boot);
  });

  document.addEventListener(
    'dialog:open',
    (event) => {
      if (!event.target?.closest?.('cart-drawer-component')) return;
      requestAnimationFrame(boot);
    },
    true
  );
})();
